import React, { useState, useCallback } from 'react';
import './grid.scss';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Styles from './gridStyle.module.scss';
import { useAgentHeartBeatService } from '../services/agentHeartBeatService';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import PatchDetailsModal from '../Modal/PatchDetailsModal';

export interface AgentHeartbeat {
  tenantAgentId: string;
  tenantId: string;
  agentStatus: string;
  heartbeatStatusMessage: string;
  hostName: string;
  id: string;
  _ts: number;
  schemaVersion: string;
}

export interface HeartbeatResponse {
  data: AgentHeartbeat[];
  message: string;
  isSuccess: boolean;
  errorCode: string | null;
}

interface RowData {
  patchName: { name: string; image: string; packageIdentifier?: string };
  remediated: string;
  deviceImpactedTotal: string;
  agentList: string[];
  type: 'thirdParty' | 'osUpdate';
  description?: string;
  releaseDate?: string;
  severity?: string;
  cveScore?: string;
  osUpdateId?: string;
  packageVersion?: string;
  urlFilter?: string;
  latestVersionKey?: string;
}

interface GridProps {
  heading: string;
  rowArray: RowData[];
}

const Grid: React.FC<GridProps> = ({ heading, rowArray }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<RowData | null>(null);
  const [heartbeatResponse, setHeartbeatResponse] = useState<HeartbeatResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { getAgentHeartbeatStatus } = useAgentHeartBeatService();
  const { selectedTenantId } = useTenantSelection();

  // ⭐ Added: Pad the table to always have 5 rows
  const maxRows = 5;
  const paddedRows: RowData[] = [
    ...rowArray,
    ...Array(Math.max(0, maxRows - rowArray.length)).fill({
      patchName: { name: '', image: '' },
      remediated: '',
      deviceImpactedTotal: '',
      agentList: [],
      type: 'thirdParty',
    }),
  ];

  const handleIconClick = useCallback((row: RowData) => {
    if (!row.patchName.name) return; // Ignore placeholder rows

    setSelectedRow(row);
    setShowModal(true);
    setHeartbeatResponse(null);

    if (row.agentList && row.agentList.length > 0) {
      fetchAgents(row.agentList);
    } else {
      console.warn(`No agents found for patch: ${row.patchName.name}`);
    }
  }, []);

  const fetchAgents = async (agentList: string[]) => {
    setIsLoading(true);
    try {
      // const response: HeartbeatResponse = await getAgentHeartbeatStatus(selectedTenantId as string, agentList);
      // setHeartbeatResponse(response);
      setHeartbeatResponse(null); // Placeholder for now
    } catch (error) {
      console.error('Error fetching agent heartbeat status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
    setHeartbeatResponse(null);
  };

  const columnDefs: ColDef<RowData>[] = [
    {
      headerName: 'Patch Name',
      field: 'patchName',
      flex: 1,
      cellRenderer: (params: any) => {
        const { name } = params.value;
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span title={name}>{name}</span>
          </div>
        );
      },
    },
    {
      headerName: 'Impacted',
      field: 'deviceImpactedTotal',
      headerClass: 'ag-center-header-envc',
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      cellRenderer: (params: any) => {
        const { deviceImpactedTotal, remediated } = params.data;
        if (!params.data.patchName.name) return ''; // Empty placeholder row

        const safeValue = deviceImpactedTotal?.toString() || '';
        const parts = safeValue.trim().split(' ');
        const number = parts[0] || '';
        const label = parts.slice(1).join(' ');

        const textColor = remediated === '100%' ? 'rgba(8, 165, 50, 1)' : 'rgba(165, 8, 17, 1)';

        return (
          <span>
            <span style={{ textAlign: 'center', marginRight: 4 }}>{number}</span>
            <span style={{ color: textColor }}>{label}</span>
          </span>
        );
      },
      width: 140,
    },
    {
      headerName: 'Action',
      field: 'remediated',
      width: 140,
      headerClass: 'ag-center-header-envc',
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      cellRenderer: (params: any) => {
        if (!params.data.patchName.name) return ''; // Empty placeholder row

        const iconSrc = params.value === '100%' ? require('../../icons/greenAction.png') : require('../../icons/redAction.png');

        return (
          <img
            src={iconSrc}
            alt="Action"
            width="22"
            height="22"
            style={{ cursor: 'pointer' }}
            onClick={() => handleIconClick(params.data)}
          />
        );
      },
    },
  ];

  const rowClassRules = {
    'row-empty': (params: any) => !params.data.patchName.name,
    'row-default': () => true,
  };

  return (
    <>
      <h3>{heading}</h3>
      <div className={`${Styles.underline}`}></div>

      <div className="ag-theme-alpine custom-grid" style={{ height: 300, width: '100%' }}>
        <AgGridReact<RowData>
          rowData={paddedRows}
          columnDefs={columnDefs}
          rowClassRules={rowClassRules}
          suppressCellFocus={true}
          // domLayout="autoHeight"
        />
      </div>

      <PatchDetailsModal
        show={showModal}
        selectedRow={selectedRow}
        heartbeatResponse={heartbeatResponse}
        isLoading={isLoading}
        onClose={closeModal}
      />
    </>
  );
};

export default Grid;
